import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPage.css';
import SEOHelmet from '../components/SEOHelmet';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <SEOHelmet 
        title="Privacy Policy - Stratagease"
        description="Our privacy policy explains how Stratagease collects, uses, and protects your personal information when you use our services."
      />
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Home</Link>
        
        <h1>Privacy Policy</h1>
        <p className="legal-intro">Protecting customers personal information is one of our highest priorities.</p>
        <p className="legal-intro">If you have questions about our privacy policy please <a href="mailto:support@stratagease.com">email us</a>.</p>

        <div className="legal-content">
          <h2>Personal Information Protection Policy</h2>
          <p className="legal-subtitle">PRIVACY NOTICE</p>
          <p className="legal-date">Last updated September 13, 2022</p>

          <p>This privacy notice for Stratagease Ltd (doing business as Stratagease) ('Stratagease', 'we', 'us', or 'our',), describes how and why we might collect, store, use, and/or share ('process') your information when you use our services ('Services'), such as when you:</p>

          <ul>
            <li>Visit our website at <a href="https://www.stratagease.com" target="_blank" rel="noopener noreferrer">https://www.stratagease.com</a>, or any website of ours that links to this privacy notice</li>
            <li>Download and use our mobile application (Marketing Strategy), or any other application of ours that links to this privacy notice</li>
            <li>Engage with us in other related ways, including any sales, marketing, or events</li>
          </ul>

          <p><strong>Questions or concerns?</strong> Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:mail@stratagease.com">mail@stratagease.com</a>.</p>

          <h3>SUMMARY OF KEY POINTS</h3>
          <p>This summary provides key points from our privacy notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</p>

          <p><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with Stratagease and the Services, the choices you make, and the products and features you use.</p>

          <p><strong>Do we process any sensitive personal information?</strong> We may process sensitive personal information when necessary with your consent or as otherwise permitted by applicable law.</p>

          <p><strong>Do we receive any information from third parties?</strong> We do not receive any information from third parties.</p>

          <p><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so.</p>

          <p><strong>In what situations and with which types of parties do we share personal information?</strong> We may share information in specific situations and with specific categories of third parties.</p>

          <p><strong>How do we keep your information safe?</strong> We have organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.</p>

          <p><strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.</p>

          <p><strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.</p>

          <h3>TABLE OF CONTENTS</h3>
          <ol className="toc">
            <li>WHAT INFORMATION DO WE COLLECT?</li>
            <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
            <li>WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?</li>
            <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
            <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
            <li>IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</li>
            <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
            <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
            <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
            <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
            <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
            <li>DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
            <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
            <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            <li>HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</li>
          </ol>

          <h3>1. WHAT INFORMATION DO WE COLLECT?</h3>
          <h4>Personal information you disclose to us</h4>
          <p><em>In Short: We collect personal information that you provide to us.</em></p>
          <p>We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>

          <h4>Personal Information Provided by You</h4>
          <p>The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:</p>
          <ul>
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>job titles</li>
            <li>mailing addresses</li>
            <li>usernames</li>
            <li>passwords</li>
            <li>contact preferences</li>
            <li>contact or authentication data</li>
            <li>billing addresses</li>
            <li>debit/credit card numbers</li>
          </ul>

          <h4>Sensitive Information</h4>
          <p>When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information:</p>
          <ul>
            <li>financial data</li>
          </ul>

          <h4>Payment Data</h4>
          <p>We may collect data necessary to process your payment if you make purchases, such as your payment instrument number (such as a credit card number), and the security code associated with your payment instrument. All payment data is stored by Stripe, Apple and Google. You may find their privacy notice link(s) here: <a href="https://stripe.com/en-ca/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy</a>, <a href="https://www.apple.com/legal/privacy/en-ww/" target="_blank" rel="noopener noreferrer">Apple Privacy</a> and <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer">Google Privacy</a>.</p>

          <p>For complete details on information collection, data processing, legal bases, sharing practices, cookies, international transfers, data retention, security measures, and your privacy rights, please contact us at <a href="mailto:support@stratagease.com">support@stratagease.com</a>.</p>

          <h3>14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h3>
          <p>If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO), Dr Raewyn Sleeman, by email at <a href="mailto:mail@stratagease.com">mail@stratagease.com</a></p>

          <h3>15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h3>
          <p>Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request to review, update, or delete your personal information, please email <a href="mailto:support@stratagease.com">support@stratagease.com</a>.</p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;